// 스프라이트 묶음: 텍스처·블렌드 한 쌍을 InstancedMesh 하나(드로우콜 1회)로 그린다. 살아 있는 장은 앞쪽에 모아 두고(죽으면 마지막 장과 자리 교환),
// 매 프레임 살아 있는 장의 행렬·색·불투명도·획 진행을 인스턴스 버퍼에 쓴다. 생성 뒤에는 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { makeBatchMaterial, type BlendKind } from './material';
import type { SpriteSpec } from './specs';

interface Slot {
  spec: SpriteSpec | null;
  t: number;
  x: number; y: number; z: number;
  facing: number; fx: number; fz: number;
  k: number; roll: number; flip: boolean;
  /** 가로 배율(세로 대비). 길쭉한 흔적·선분에 쓴다 */
  ka: number;
  /** 선형 색(스펙 tint를 생성 때 한 번 변환) */
  r: number; g: number; b: number;
}

const tmpColor = new THREE.Color();

export class SpriteBatch {
  readonly mesh: THREE.InstancedMesh;
  readonly cap: number;
  /** 살아 있는 장 수(앞에서부터 n장) */
  n = 0;
  private readonly slots: Slot[] = [];
  private readonly attrs: { tint: THREE.InstancedBufferAttribute; ctrl: THREE.InstancedBufferAttribute; axis: THREE.InstancedBufferAttribute };
  private readonly map: { value: THREE.Texture };
  private readonly dummy = new THREE.Object3D();
  private readonly fwd = new THREE.Vector3();
  private prewarming = false;

  constructor(cap: number, readonly blend: BlendKind, initial: THREE.Texture) {
    this.cap = cap;
    const tint = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const ctrl = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const axis = new THREE.InstancedBufferAttribute(new Float32Array(cap * 2), 2);
    tint.setUsage(THREE.DynamicDrawUsage); ctrl.setUsage(THREE.DynamicDrawUsage); axis.setUsage(THREE.DynamicDrawUsage);
    this.attrs = { tint, ctrl, axis };
    const { material, map } = makeBatchMaterial(initial, blend, this.attrs);
    this.map = map;
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), material, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.renderOrder = blend === 'alpha' ? 1 : 2;
    this.dummy.rotation.order = 'YXZ';
    for (let i = 0; i < cap; i++) {
      this.slots.push({ spec: null, t: 0, x: 0, y: 0, z: 0, facing: 0, fx: 0, fz: 1, k: 1, roll: 0, flip: false, ka: 1, r: 1, g: 1, b: 1 });
    }
  }

  setTexture(t: THREE.Texture): void { this.map.value = t; }

  get free(): number { return this.cap - this.n; }

  /** 한 장을 띄운다. 묶음이 가득 차면 false(프레임 안정 우선으로 버린다) */
  spawn(spec: SpriteSpec, x: number, y: number, z: number, facing: number, scale: number, roll: number, flip: boolean, stretch = 1): boolean {
    if (this.n >= this.cap) return false;
    const s = this.slots[this.n++];
    s.spec = spec; s.t = 0;
    s.x = x; s.y = y; s.z = z;
    s.facing = facing; s.fx = Math.sin(facing); s.fz = Math.cos(facing);
    s.k = scale; s.roll = roll; s.flip = flip; s.ka = stretch;
    tmpColor.setHex(spec.tint ?? 0xffffff);
    s.r = tmpColor.r; s.g = tmpColor.g; s.b = tmpColor.b;
    return true;
  }

  update(dt: number, camera: THREE.PerspectiveCamera): void {
    if (this.prewarming) return;
    const tint = this.attrs.tint.array as Float32Array;
    const ctrl = this.attrs.ctrl.array as Float32Array;
    const axis = this.attrs.axis.array as Float32Array;
    const d = this.dummy;
    let i = 0;
    while (i < this.n) {
      const s = this.slots[i];
      const spec = s.spec!;
      s.t += dt;
      if (s.t >= spec.duration) {
        // 마지막 장과 자리를 바꿔 앞쪽을 빽빽하게 유지한다(객체 교환만, 할당 없음)
        const j = --this.n;
        s.spec = null;
        if (i !== j) { this.slots[i] = this.slots[j]; this.slots[j] = s; }
        continue;
      }
      const u = s.t / spec.duration;
      const k = (spec.scale ? spec.scale(u) : 1) * s.k;

      // 위치
      let px = s.x, py = s.y, pz = s.z;
      if (spec.rise) py += spec.rise * s.t;
      if (spec.move) { px += s.fx * spec.move * s.t; pz += s.fz * spec.move * s.t; }
      if (spec.fall) { const r = 1 - u; px -= s.fx * spec.fall[0] * r; pz -= s.fz * spec.fall[0] * r; py += spec.fall[1] * r; }
      d.position.set(px, py, pz);

      // 방향
      const spin = (spec.spin ?? 0) * s.t + s.roll;
      switch (spec.orient) {
        case 'billboard':
          d.quaternion.copy(camera.quaternion);
          d.rotateZ(spin);
          break;
        case 'ground':
          d.rotation.set(-Math.PI / 2, 0, spin);
          break;
        case 'pillar':
          d.rotation.set(0, Math.atan2(camera.position.x - px, camera.position.z - pz), 0);
          break;
        case 'facing':
          // 평면을 눕힌 뒤(텍스처 위쪽이 진행 방향을 향하도록 +PI) 기울인다
          d.rotation.set(-Math.PI / 2 + (spec.tilt ?? 0), s.facing + Math.PI, spin);
          break;
      }
      d.scale.set(spec.size[0] * k * s.ka * (s.flip ? -1 : 1), spec.size[1] * k, 1);
      d.updateMatrix();
      this.mesh.setMatrixAt(i, d.matrix);

      // 획 그리기 / 소멸
      let head = 10, tail = -10;
      const rv = spec.reveal;
      if (rv) {
        head = Math.min(s.t / rv.draw, 1) * 1.1;
        tail = s.t < rv.fadeAt ? -1 : -1 + Math.min((s.t - rv.fadeAt) / rv.fade, 1) * 2.3;
      }
      const o4 = i * 4;
      tint[o4] = s.r; tint[o4 + 1] = s.g; tint[o4 + 2] = s.b; tint[o4 + 3] = spec.intensity ?? 1;
      ctrl[o4] = spec.opacity ? spec.opacity(u) : 1; ctrl[o4 + 1] = head; ctrl[o4 + 2] = tail; ctrl[o4 + 3] = spec.recolor ?? 0;
      axis[i * 2] = rv ? rv.axis[0] : 0.5; axis[i * 2 + 1] = rv ? rv.axis[1] : 0.5;
      i++;
    }
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    if (this.n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.attrs.tint.needsUpdate = true;
      this.attrs.ctrl.needsUpdate = true;
      this.attrs.axis.needsUpdate = true;
    }
  }

  /** 미리 컴파일용: 한 장을 보이게 둔다(불투명도 0) */
  prewarm(on: boolean): void {
    this.prewarming = on;
    if (on) {
      this.dummy.position.set(0, -50, 0); this.dummy.rotation.set(0, 0, 0); this.dummy.scale.set(0.01, 0.01, 1);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(0, this.dummy.matrix);
      (this.attrs.ctrl.array as Float32Array).fill(0, 0, 4);
      this.mesh.instanceMatrix.needsUpdate = true; this.attrs.ctrl.needsUpdate = true;
      this.mesh.count = 1; this.mesh.visible = true;
    } else {
      this.mesh.count = this.n; this.mesh.visible = this.n > 0;
    }
  }

  clear(): void {
    for (let i = 0; i < this.n; i++) this.slots[i].spec = null;
    this.n = 0;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
  }
}

